import { CompilationPreview } from "@turtlewave/types";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useCallback, useEffect, useState } from "react";
import {
  TbArrowNarrowLeft,
  TbArrowNarrowRight,
  TbLayoutGrid,
} from "react-icons/tb";
import { Link } from "react-router";

import { Loadable } from "../../types/loadable";

import { CompilationCard } from "./CompilationCard";

interface CompilationsListProps {
  query: Loadable<CompilationPreview[]>;
  variant?: "grid" | "carousel";
}

export function CompilationsList({
  query,
  variant = "grid",
}: CompilationsListProps) {
  if (query.isLoading) {
    return variant === "carousel" ? (
      <CompilationsCarouselSkeleton />
    ) : (
      <CompilationsGridSkeleton />
    );
  }

  const compilations = query.data;
  if (!compilations || compilations.length === 0) {
    return null;
  }

  if (variant === "carousel") {
    return <CompilationsCarousel compilations={compilations} />;
  }
  return <CompilationsGrid compilations={compilations} />;
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

const SKELETON_COUNT = 6;

function CompilationCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={className || "w-full"}>
      <div className="skeleton-light-bg animate-pulse rounded-3xl p-4 sm:p-4.5">
        <div className="flex flex-col gap-1 sm:gap-2 lg:gap-3">
          <div className="aspect-5/3" />
          <div className="flex flex-row gap-1 sm:gap-2 lg:gap-3">
            <div className="aspect-square flex-1" />
            <div className="aspect-square flex-1" />
            <div className="aspect-square flex-1" />
          </div>
        </div>
        <div className="mt-2 md:mt-4">
          <div className="h-5" />
        </div>
      </div>
    </div>
  );
}

function CompilationsGridSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(9.375rem,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(10.9375rem,1fr))] sm:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(12.5rem,1fr))] lg:gap-5">
      {Array.from({ length: SKELETON_COUNT }, (_, i) => (
        <CompilationCardSkeleton key={i} />
      ))}
    </div>
  );
}

function CompilationsCarouselSkeleton() {
  return (
    <div>
      <div className="flex gap-3 overflow-hidden sm:gap-4 lg:gap-5">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <CompilationCardSkeleton
            key={i}
            className="flex-[0_0_clamp(10.9375rem,25vw,15.625rem)]"
          />
        ))}
      </div>
      {/* Controls placeholder */}
      <div className="mt-8 flex items-center justify-end gap-4 md:gap-6 lg:mt-10">
        <div className="skeleton-light-bg size-12 animate-pulse rounded-full md:size-13" />
        <div className="flex gap-2 md:gap-3">
          <div className="skeleton-light-bg size-12 animate-pulse rounded-full md:size-13" />
          <div className="skeleton-light-bg size-12 animate-pulse rounded-full md:size-13" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid variant — wrapping responsive grid, centered
// ---------------------------------------------------------------------------

function CompilationsGrid({
  compilations,
}: {
  compilations: CompilationPreview[];
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(9.375rem,1fr))] gap-3 sm:grid-cols-[repeat(auto-fill,minmax(10.9375rem,1fr))] sm:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(12.5rem,1fr))] lg:gap-5">
      {compilations.map((compilation) => (
        <CompilationCard
          key={compilation.compilation_id}
          compilation={compilation}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carousel variant — Embla-powered horizontal scrollable row
// ---------------------------------------------------------------------------

const controlBase =
  "flex size-12 md:size-13 items-center justify-center rounded-full border-2 md:border-[2.5] border-nav-dark transition-all duration-200 ease-out bg-transparent text-nav-dark hover:bg-nav-dark hover:text-nav-light active:bg-nav-light active:text-nav-dark active:border-nav-light hover:shadow-md hover:shadow-blue-950/10 active:shadow-lg active:shadow-blue-950/15";

const controlDisabled =
  "cursor-pointer disabled:pointer-events-none disabled:border-neutral-600 disabled:text-neutral-600";

function CompilationsCarousel({
  compilations,
}: {
  compilations: CompilationPreview[];
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { align: "start", containScroll: "trimSnaps", dragFree: true },
    [WheelGesturesPlugin()],
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [fadeLeft, setFadeLeft] = useState(0);
  const [fadeRight, setFadeRight] = useState(0);

  const MAX_FADE_PX = 10;

  const updateScrollState = useCallback(() => {
    if (!emblaApi) return;
    const canPrev = emblaApi.canScrollPrev();
    const canNext = emblaApi.canScrollNext();
    setCanScrollPrev(canPrev);
    setCanScrollNext(canNext);

    // Fade sizes scale proportionally with scroll distance,
    // capped at MAX_FADE_PX. Uses translateX from Embla's transform-based
    // positioning (scrollLeft is always 0 because Embla uses translate3d).
    const viewport = emblaApi.rootNode();
    const container = emblaApi.containerNode();
    const viewportWidth = viewport.clientWidth;
    const matrix = new DOMMatrixReadOnly(getComputedStyle(container).transform);
    const currentX = Math.abs(matrix.m41);
    const maxTranslate = container.scrollWidth - viewportWidth;

    setFadeLeft(Math.min(MAX_FADE_PX, Math.max(0, currentX)));
    setFadeRight(Math.min(MAX_FADE_PX, Math.max(0, maxTranslate - currentX)));
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    updateScrollState();
    emblaApi.on("select", updateScrollState);
    emblaApi.on("reInit", updateScrollState);
    emblaApi.on("scroll", updateScrollState);
    return () => {
      emblaApi.off("select", updateScrollState);
      emblaApi.off("reInit", updateScrollState);
      emblaApi.off("scroll", updateScrollState);
    };
  }, [emblaApi, updateScrollState]);

  // Prevent the page-level swipe handler from capturing touch events that
  // originate over the carousel cards while the carousel is scrollable, so a
  // horizontal drag scrolls the cards instead of navigating pages. Attached to
  // the Embla viewport only (not the wrapper that also holds the controls row
  // below it), so page swipe still works from the controls area and the gap
  // beneath the cards. When all cards fit, propagation is allowed so page swipe
  // works from the cards too.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== "touch") return;
      if (canScrollPrev || canScrollNext) {
        e.stopPropagation();
      }
    },
    [canScrollPrev, canScrollNext],
  );

  const isFirefox =
    typeof navigator !== "undefined" && /Firefox/i.test(navigator.userAgent);

  const maskStyle = (() => {
    if (isFirefox || (fadeLeft === 0 && fadeRight === 0)) return undefined;
    const l = `${fadeLeft}px`;
    const r = `${fadeRight}px`;
    if (fadeLeft > 0 && fadeRight > 0) {
      return {
        maskImage: `linear-gradient(to right, transparent, black ${l}, black calc(100% - ${r}), transparent)`,
      };
    }
    if (fadeLeft > 0) {
      return {
        maskImage: `linear-gradient(to right, transparent, black ${l})`,
      };
    }
    return { maskImage: `linear-gradient(to left, transparent, black ${r})` };
  })();

  return (
    <div>
      <div
        className="embla-viewport isolate"
        style={maskStyle}
        ref={emblaRef}
        onPointerDown={handlePointerDown}
      >
        <div className="embla-container gap-3 sm:gap-4 lg:gap-5">
          {compilations.map((compilation) => (
            <CompilationCard
              key={compilation.compilation_id}
              compilation={compilation}
              sizeClassName="embla-slide flex-[0_0_clamp(10.9375rem,25vw,15.625rem)]"
            />
          ))}
        </div>
      </div>

      {/* Controls row */}
      <div className="relative z-10 mt-8 flex items-center justify-end gap-4 md:gap-6 lg:mt-10">
        <Link
          to="/compilations"
          className={`tooltip tooltip-top tooltip-quick ${controlBase}`}
          data-tip="See all compilations"
          aria-label="See all compilations"
        >
          <TbLayoutGrid className="size-8 md:size-9" />
        </Link>

        <div className="flex gap-2 md:gap-3">
          <button
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            className={`${controlBase} ${controlDisabled}`}
            aria-label="Scroll compilations left"
          >
            <TbArrowNarrowLeft className="size-8 md:size-9" />
          </button>

          <button
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            className={`${controlBase} ${controlDisabled}`}
            aria-label="Scroll compilations right"
          >
            <TbArrowNarrowRight className="size-8 md:size-9" />
          </button>
        </div>
      </div>
    </div>
  );
}
